/**
 * Phone normalization for India / WhatsApp wa.me links.
 *
 * Handles common dirty inputs:
 *  - "+91 6305885757", "+91-6305885757", "91-6305885757"
 *  - "06305885757" (leading STD 0)
 *  - Devanagari / Indic numerals (६३०५८८५७५७)
 *  - Zero-width spaces & non-breaking spaces hidden inside the string
 *  - Numbers stored as JS Number that overflow / lose leading 0s
 *
 * Returns a clean E.164-style string suitable for wa.me/, e.g. "916305885757".
 * Returns "" if we can't recover a valid number.
 */
const INDIC_DIGIT_MAPS = [
  // Devanagari ० १ २ ३ ४ ५ ६ ७ ८ ९
  ['\u0966', '\u0967', '\u0968', '\u0969', '\u096A', '\u096B', '\u096C', '\u096D', '\u096E', '\u096F'],
  // Telugu ౦ ౧ ౨ ౩ ౪ ౫ ౬ ౭ ౮ ౯
  ['\u0C66', '\u0C67', '\u0C68', '\u0C69', '\u0C6A', '\u0C6B', '\u0C6C', '\u0C6D', '\u0C6E', '\u0C6F'],
  // Tamil ௦ ௧ ௨ ௩ ௪ ௫ ௬ ௭ ௮ ௯
  ['\u0BE6', '\u0BE7', '\u0BE8', '\u0BE9', '\u0BEA', '\u0BEB', '\u0BEC', '\u0BED', '\u0BEE', '\u0BEF'],
  // Bengali ০ ১ ২ ৩ ৪ ৫ ৬ ৭ ৮ ৯
  ['\u09E6', '\u09E7', '\u09E8', '\u09E9', '\u09EA', '\u09EB', '\u09EC', '\u09ED', '\u09EE', '\u09EF'],
  // Arabic-Indic ٠ ١ ٢ ٣ ٤ ٥ ٦ ٧ ٨ ٩
  ['\u0660', '\u0661', '\u0662', '\u0663', '\u0664', '\u0665', '\u0666', '\u0667', '\u0668', '\u0669'],
];

const indicToAscii = (s) => {
  let out = String(s);
  for (const map of INDIC_DIGIT_MAPS) {
    for (let i = 0; i < 10; i++) {
      if (out.indexOf(map[i]) !== -1) {
        out = out.split(map[i]).join(String(i));
      }
    }
  }
  return out;
};

/**
 * Returns an object with `clean` (just 10 digits, no country code) and
 * `e164` (with 91 country code, suitable for wa.me/), and `ok` boolean.
 */
export function normalizeIndianPhone(raw) {
  if (raw === null || raw === undefined) return { ok: false, clean: '', e164: '' };
  // 1. Convert to ASCII string, normalize Indic numerals, strip zero-width
  //    and other invisible characters before the digit-only regex runs.
  let s = indicToAscii(String(raw));
  // Strip zero-width / non-breaking / control whitespace
  s = s.replace(/[\u200B-\u200F\u2028\u2029\uFEFF\u00A0]/g, '');
  // Strip every non-digit character
  let digits = s.replace(/[^0-9]/g, '');
  if (!digits) return { ok: false, clean: '', e164: '' };

  // Drop a leading 0 (Indian STD prefix) — e.g. 09876543210 → 9876543210
  digits = digits.replace(/^0+/, '');

  // Drop a country code 91 if it's already there & length is ≥12
  if (digits.length === 12 && digits.startsWith('91')) {
    return { ok: true, clean: digits.slice(2), e164: digits };
  }
  if (digits.length === 13 && digits.startsWith('091')) {
    const c = digits.slice(3);
    return { ok: true, clean: c, e164: '91' + c };
  }
  if (digits.length === 11 && digits.startsWith('0')) {
    const c = digits.slice(1);
    return { ok: true, clean: c, e164: '91' + c };
  }
  // 10 digits — assume India and prefix +91
  if (digits.length === 10) {
    return { ok: true, clean: digits, e164: '91' + digits };
  }
  // Final fallback — keep last 10 digits (handles "+91-6305885757", "9163058857571" etc.)
  if (digits.length > 10) {
    const last10 = digits.slice(-10);
    if (last10.length === 10) return { ok: true, clean: last10, e164: '91' + last10 };
  }
  return { ok: false, clean: digits, e164: digits };
}

/**
 * Returns a fully-qualified wa.me/ URL for the given phone + message.
 * Returns '' if the phone can't be normalized to a valid Indian number.
 */
export function waLink(rawPhone, message = '') {
  const { ok, e164 } = normalizeIndianPhone(rawPhone);
  if (!ok) return '';
  const q = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${e164}${q}`;
}

/** Returns a tel: link with the clean E.164 number for native dialer. */
export function telLink(rawPhone) {
  const { ok, e164 } = normalizeIndianPhone(rawPhone);
  if (!ok) return '';
  return `tel:+${e164}`;
}
